import { Component, DestroyRef, inject, Input, OnInit } from '@angular/core';
import { OnboardingService } from '../onboarding.service';
import { FormBuilder, Validators } from '@angular/forms';
import { ArivoStepperComponent } from '@shared/components/ui-elements';
import { LicensePlate, ProviderLinkOptions } from '@shared/models';
import { fbOptionalString, showSpinner } from '@shared/utils';
import { ToastService } from '@shared/services';
import { TranslateService } from '@ngx-translate/core';
import { ArivoMarketingConsentService } from '@shared/components';
import { AmplitudeEventService, FeatureFlagService, ProviderSettingsService } from '@core/services';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { RegistrationEmailSubmitted, RegistrationResendEmailClicked } from '@integrations/amplitude';

@Component({
  selector: 'app-onboarding-third-step',
  templateUrl: './onboarding-third-step.component.html',
  styleUrl: './onboarding-third-step.component.scss',
})
export class OnboardingThirdStepComponent implements OnInit {
  @Input() stepperComponent?: ArivoStepperComponent;

  destroyRef = inject(DestroyRef);
  emailSent: boolean = false;
  email?: string;
  marketingConsentEnabled!: boolean;
  providerLinks!: ProviderLinkOptions;

  constructor(
    private _formBuilder: FormBuilder,
    private translateService: TranslateService,
    private onboardingService: OnboardingService,
    private toastService: ToastService,
    private marketingConsentService: ArivoMarketingConsentService,
    private providerSettingsService: ProviderSettingsService,
    private featureFlagService: FeatureFlagService,
    private amplitudeEventService: AmplitudeEventService,
  ) {}

  ngOnInit() {
    this.marketingConsentEnabled = this.featureFlagService.getFeatureFlag('enable_marketing_consent');

    this.providerSettingsService.providerSettings.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.providerLinks = this.providerSettingsService.getLinks();
    });
  }

  get publicProductsPageEnabled(): boolean {
    return this.onboardingService.publicProductsPageEnabled;
  }

  get licensePlate(): LicensePlate | null {
    return this.onboardingService.licensePlate.getValue();
  }

  signUpForm = this._formBuilder.group({
    email: [fbOptionalString, [Validators.required, Validators.email]],
    termsAndConditions: [false, [Validators.requiredTrue]],
    marketingConsent: [false],
  });

  previous() {
    if (this.stepperComponent) {
      this.stepperComponent.selectedIndex = 0;
    }
  }

  sendEmailAgain() {
    this.next(true);
  }

  next(resendEmail: boolean = false) {
    this.signUpForm.markAllAsTouched();
    if (this.signUpForm.valid && this.licensePlate) {
      this.onboardingService
        .sendOnboardingEmail(this.licensePlate, this.signUpForm.value.email!, this.signUpForm.value.marketingConsent!)
        .pipe(showSpinner())
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: () => {
            if (!resendEmail) {
              this.amplitudeEventService.trackEvent(
                new RegistrationEmailSubmitted({
                  registrationFlowType: 'ppu',
                }),
              );
            } else {
              this.amplitudeEventService.trackEvent(
                new RegistrationResendEmailClicked({
                  registrationFlowType: 'ppu',
                }),
              );
            }
            this.emailSent = true;
            this.email = this.signUpForm.value.email!;
            this.toastService.add({
              message: this.translateService.instant('Onboarding.WeHaveSentAnEmailToast', { email: this.signUpForm.value.email! }),
              type: 'success',
            });
            if (this.signUpForm.value.marketingConsent) {
              this.marketingConsentService.submitMarketingAgreement(this.signUpForm.value.email!);
            }
          },
          error: (error) => {
            if (error.status === 429) {
              this.toastService.add({
                message: error.error.message ?? this.translateService.instant('Common.Errors.UnknownError'),
                type: 'error',
              });
            } else {
              this.toastService.add({
                message: this.translateService.instant('LogIn.CouldNotSendEmail'),
                type: 'error',
              });
            }
          },
        });
    }
  }
}
